import PropTypes from 'prop-types';
import { Link } from 'react-router';

import { fetchProductDetail } from '#/api/crmMgmt/product/fetchDetail';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import SideMsg from '#/modules/commonComponents/SideMsg';
import {
  buildProductSideMsgDetailBlock,
  productSideMsgStatusBlock,
} from '#/modules/crmMgmt/product/data/productSideMsgConfig';
import {
  canActivateProduct,
  canDeactivateProduct,
} from '#/modules/crmMgmt/product/helper/productStatus';
import useProductInUseDialog from '#/modules/crmMgmt/product/hooks/useProductInUseDialog';
import useProductStatusMutation, {
  PRODUCT_ACTIVATE_CONFIRM,
  PRODUCT_DEACTIVATE_CONFIRM,
} from '#/modules/crmMgmt/product/hooks/useProductStatusMutation';
import { useCan } from '#/modules/infra/auth/useCan';

const ProductSideMsg = ({ displayData, setSelectedData, customerLabelCache }) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const { showInUse, InUseDialogHost } = useProductInUseDialog();
  const canUpdate = useCan(PERM.PRODUCT_UPDATE);
  const canActivatePerm = useCan(PERM.PRODUCT_ACTIVATE);

  const id = displayData?.id;
  const status = displayData?.status;
  const updatedAt = displayData?.record?.updatedAt ?? displayData?.updatedAt;
  const canActivate = canActivatePerm && canActivateProduct(status);
  const canDeactivate = canActivatePerm && canDeactivateProduct(status);

  const statusMutation = useProductStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onInUse: showInUse,
    onAfterSuccess: async () => {
      if (!id) {
        return;
      }
      try {
        const detail = await fetchProductDetail(id);
        setSelectedData(detail?.data ?? null);
      } catch {
        setSelectedData(null);
      }
    },
    onVersionConflict: () => {
      if (!id) {
        return;
      }
      fetchProductDetail(id)
        .then((detail) => setSelectedData(detail?.data ?? null))
        .catch(() => setSelectedData(null));
    },
  });

  const isPending = statusMutation.isPending;
  const canOpenDetail = Boolean(id);
  const canEdit = canUpdate && Boolean(id);
  const hasActions = canOpenDetail || canEdit || canActivate || canDeactivate;

  async function handleActivate() {
    const ok = await confirm(PRODUCT_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(PRODUCT_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  const blocks = [
    productSideMsgStatusBlock,
    {
      condition: () => hasActions,
      fields: [
        {
          key: 'actions',
          label: '操作',
          render: () => (
            <div className="side_msg-row">
              <h3>操作</h3>
              <div className="flex flex-wrap items-center gap-2">
                {canOpenDetail ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.productDetail(id)}>詳情</Link>
                  </Button>
                ) : null}
                {canEdit ? (
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.productEdit(id)}>編輯</Link>
                  </Button>
                ) : null}
                {canActivate ? (
                  <Button type="button" disabled={isPending} onClick={handleActivate}>
                    啟用
                  </Button>
                ) : null}
                {canDeactivate ? (
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={isPending}
                    onClick={handleDeactivate}
                  >
                    停用
                  </Button>
                ) : null}
              </div>
            </div>
          ),
        },
      ],
    },
    buildProductSideMsgDetailBlock(customerLabelCache),
  ];

  return (
    <>
      {ConfirmDialogHost}
      {InUseDialogHost}
      <SideMsg
        title="產品"
        titleValue="name"
        displayData={displayData}
        blocks={blocks}
      />
    </>
  );
};

ProductSideMsg.propTypes = {
  displayData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
  customerLabelCache: PropTypes.instanceOf(Map),
};

export default ProductSideMsg;
